import React from 'react';
import { Empty as AntdEmpty, type EmptyProps as AntdEmptyProps } from 'antd';

export type EmptyStateProps = Omit<AntdEmptyProps, 'description'> & {
    title?: React.ReactNode;
    message?: React.ReactNode;
    action?: React.ReactNode;
};

type EmptyStateComponent = React.ForwardRefExoticComponent<
    EmptyStateProps & React.RefAttributes<HTMLDivElement>
> & {
    PRESENTED_IMAGE_DEFAULT: typeof AntdEmpty.PRESENTED_IMAGE_DEFAULT;
    PRESENTED_IMAGE_SIMPLE: typeof AntdEmpty.PRESENTED_IMAGE_SIMPLE;
};

// AntD `Empty` is a plain `React.FC` and does not accept a `ref`. The forwardRef
// shell preserves the house signature/displayName and the render function takes
// both params (props, ref) so React does not warn; `_ref` is intentionally not
// forwarded to the underlying Empty.
const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
    ({ title, message, action, children, ...props }, _ref) => {
        const description =
            title && message ? (
                <span>
                    <strong style={{ display: 'block' }}>{title}</strong>
                    <span>{message}</span>
                </span>
            ) : (
                title ?? message ?? undefined
            );

        return (
            <AntdEmpty description={description} {...props}>
                {action ?? children}
            </AntdEmpty>
        );
    },
) as EmptyStateComponent;

EmptyState.displayName = 'EmptyState';
EmptyState.PRESENTED_IMAGE_DEFAULT = (AntdEmpty as typeof AntdEmpty | undefined)
    ?.PRESENTED_IMAGE_DEFAULT as typeof AntdEmpty.PRESENTED_IMAGE_DEFAULT;
EmptyState.PRESENTED_IMAGE_SIMPLE = (AntdEmpty as typeof AntdEmpty | undefined)
    ?.PRESENTED_IMAGE_SIMPLE as typeof AntdEmpty.PRESENTED_IMAGE_SIMPLE;

export { AntdEmpty };
export default EmptyState;
